import { z } from 'zod'

import type { EntityManagerService, StorageService } from '../../services'
import { ProgressEventSchema } from '../../services/Storage/types'
import { DDEXResourceContributor, DDEXCopyright } from '../../types/DDEX'
import { AudioFile, ImageFile } from '../../types/File'
import { HashId } from '../../types/HashId'
import {
  Genre,
  Mood,
  type CreatePlaylistRequest,
  type UpdatePlaylistRequest
} from '../generated/default'
import {
  UploadTrackMetadataSchema,
  USDCPurchaseConditions
} from '../tracks/types'

export type PlaylistsApiServicesConfig = {
  entityManager?: EntityManagerService
  storage: StorageService
}

const CreatePlaylistMetadataSchema = z
  .object({
    playlistId: z.optional(HashId),
    description: z.optional(z.string().max(1000)),
    playlistName: z.string(),
    isPrivate: z.optional(z.boolean()),
    isAlbum: z.optional(z.boolean()),
    playlistImageSizesMultihash: z.optional(z.string()),
    license: z.optional(z.string()),
    mood: z.optional(z.enum(Object.values(Mood) as [Mood, ...Mood[]])),
    releaseDate: z.optional(z.date()),
    ddexReleaseIds: z.optional(z.record(z.string()).nullable()),
    ddexApp: z.optional(z.string()),
    tags: z.optional(z.string()),
    upc: z.optional(z.string()),
    artists: z.optional(z.array(DDEXResourceContributor).nullable()),
    copyrightLine: z.optional(DDEXCopyright.nullable()),
    producerCopyrightLine: z.optional(DDEXCopyright.nullable()),
    parentalWarningType: z.optional(z.string().nullable()),
    isImageAutogenerated: z.optional(z.boolean()).nullable(),
    playlistContents: z.optional(
      z.array(
        z.object({
          timestamp: z.number(),
          metadataTimestamp: z.optional(z.number()),
          trackId: HashId
        })
      )
    ),

    // Album fields
    isStreamGated: z.optional(z.boolean()),
    streamConditions: z.optional(USDCPurchaseConditions).nullable(),
    isDownloadGated: z.optional(z.boolean()),
    downloadConditions: z.optional(USDCPurchaseConditions).nullable(),
    isScheduledRelease: z.optional(z.boolean())
  })
  .strict()

export type CreatePlaylistMetadata = z.input<
  typeof CreatePlaylistMetadataSchema
>

export const UploadPlaylistProgressEventSchema = ProgressEventSchema.extend({
  /**
   * Index of the track being uploaded in the playlist tracks array, or 'image' if for the image
   */
  key: z.number().or(z.literal('image'))
})

export const UploadPlaylistProgressHandlerSchema = z
  .function()
  .args(z.number(), UploadPlaylistProgressEventSchema)
  .returns(z.void())

export const CreatePlaylistSchema = z
  .object({
    playlistId: z.optional(HashId),
    imageFile: z.optional(ImageFile),
    metadata: CreatePlaylistMetadataSchema,
    onProgress: UploadPlaylistProgressHandlerSchema.optional(),
    trackIds: z.optional(z.array(HashId)),
    userId: HashId
  })
  .strict()

export type EntityManagerCreatePlaylistRequest = z.input<
  typeof CreatePlaylistSchema
>

export const UploadPlaylistMetadataSchema = CreatePlaylistMetadataSchema.extend(
  {
    genre: z.enum(Object.values(Genre) as [Genre, ...Genre[]])
  }
).strict()

// Base type from schema (used for external API)
type PlaylistMetadataBase = z.input<typeof CreatePlaylistMetadataSchema>

// Extended type that supports both string (HashId) and number (internal ID) for trackId
export type PlaylistMetadata = Omit<
  PlaylistMetadataBase,
  'playlistContents'
> & {
  playlistContents?: Array<{
    timestamp: number
    metadataTimestamp?: number
    trackId: string | number
  }>
}

const PlaylistTrackMetadataSchema = UploadTrackMetadataSchema.partial({
  genre: true,
  mood: true,
  tags: true
})

/**
 * PlaylistTrackMetadata is less strict than TrackMetadata because
 * `genre`, `mood`, and `tags` are optional
 */
export type PlaylistTrackMetadata = z.infer<typeof PlaylistTrackMetadataSchema>

/**
 * The progress event for updating a playlist
 */
type UploadPlaylistProgressEvent = z.input<
  typeof UploadPlaylistProgressEventSchema
>

export type UploadPlaylistProgressHandler = (
  /**
   * Overall progress percentage (0-1)
   */
  progress: number,
  /**
   * The progress event
   */
  event: UploadPlaylistProgressEvent
) => void

export const UploadPlaylistSchema = z
  .object({
    userId: HashId,
    imageFile: ImageFile,
    metadata: UploadPlaylistMetadataSchema,
    onProgress: z.optional(UploadPlaylistProgressHandlerSchema),
    /**
     * Track metadata is populated from the playlist if fields are missing
     */
    trackMetadatas: z.array(PlaylistTrackMetadataSchema),
    audioFiles: z.array(AudioFile)
  })
  .strict()

export type UploadPlaylistRequest = Omit<
  z.input<typeof UploadPlaylistSchema>,
  'onProgress'
> & {
  onProgress?: UploadPlaylistProgressHandler
}

export const UpdatePlaylistMetadataSchema =
  UploadPlaylistMetadataSchema.partial()
    .merge(
      z.object({
        isPrivate: z.optional(z.boolean()),
        playlistContents: z.optional(
          z.array(
            z.object({
              timestamp: z.number(),
              metadataTimestamp: z.optional(z.number()),
              trackId: HashId
            })
          )
        ),
        playlistImageSizesMultihash: z.optional(z.string())
      })
    )
    .strict()

export const UpdatePlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId,
    imageFile: z.optional(ImageFile),
    metadata: UpdatePlaylistMetadataSchema,
    onProgress: UploadPlaylistProgressHandlerSchema.optional()
  })
  .strict()

export type EntityManagerUpdatePlaylistRequest = z.input<
  typeof UpdatePlaylistSchema
>

export type PlaylistImageParameters = {
  imageFile?: z.input<typeof ImageFile>
  onProgress?: UploadPlaylistProgressHandler
}

export type CreatePlaylistRequestWithFiles = CreatePlaylistRequest &
  PlaylistImageParameters

export type UpdatePlaylistRequestWithImage = UpdatePlaylistRequest &
  PlaylistImageParameters

export const PublishPlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId
  })
  .strict()

export type PublishPlaylistRequest = z.input<typeof PublishPlaylistSchema>

export const AddTrackToPlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId,
    trackId: HashId
  })
  .strict()

export type AddTrackToPlaylistRequest = z.input<typeof AddTrackToPlaylistSchema>

export const RemoveTrackFromPlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId,
    trackIndex: z.number()
  })
  .strict()

export type RemoveTrackFromPlaylistRequest = z.input<
  typeof RemoveTrackFromPlaylistSchema
>

export const DeletePlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId
  })
  .strict()

export type EntityManagerDeletePlaylistRequest = z.input<
  typeof DeletePlaylistSchema
>

export const FavoritePlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId,
    metadata: z.optional(
      z
        .object({
          /**
           * Is this a save of a repost? Used to dispatch notifications
           * when a user favorites another user's repost
           */
          isSaveOfRepost: z.optional(z.boolean())
        })
        .strict()
    )
  })
  .strict()

export type EntityManagerFavoritePlaylistRequest = z.input<
  typeof FavoritePlaylistSchema
>

export const UnfavoritePlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId
  })
  .strict()

export type EntityManagerUnfavoritePlaylistRequest = z.input<
  typeof UnfavoritePlaylistSchema
>

export const RepostPlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId,
    metadata: z.optional(
      z
        .object({
          /**
           * Is this a repost of a repost? Used to dispatch notifications
           * when a user reposts content that someone they follow has already reposted
           */
          isRepostOfRepost: z.optional(z.boolean())
        })
        .strict()
    )
  })
  .strict()

export type EntityManagerRepostPlaylistRequest = z.input<
  typeof RepostPlaylistSchema
>

export const UnrepostPlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId
  })
  .strict()

export type EntityManagerUnrepostPlaylistRequest = z.input<
  typeof UnrepostPlaylistSchema
>

export const SharePlaylistSchema = z
  .object({
    userId: HashId,
    playlistId: HashId
  })
  .strict()

export type EntityManagerSharePlaylistRequest = z.input<
  typeof SharePlaylistSchema
>
